<template>
  <div class="upload-component">
    <div :class="{ 'cursor-not-allowed': disabled }">
      <input type="file" ref="fileInput" class="hidden"
        :accept="type === FILE.IMAGE.NAME ? 'image/png,image/jpeg' : 'video/mp4'" multiple @change="handleClick"
        :disabled="disabled">
      <div
        v-if="showFileInput"
        @click="$refs.fileInput.click()"
        @drop="handleDrop"
        @dragover="handleDragover"
        @dragenter="handleDragover"
        class="cursor-pointer text-center upload-component relative overflow-hidden"
        :class="[{ 'error' : localErrors.length}, placeholder.classes || 'px-8 pb-16 pt-10']"
      >
        <div class="mb-4" v-if="showTitle">
          <span class="text-lg text-black">Drag and drop or </span>
          <span class="text-lg font-medium text-primary" @click.stop="$refs.fileInput.click()">Browse</span>
        </div>

        <div class="upload-component-notes mb-3">
          <span :class="{ 'text-danger': localErrors.find(i => i === 'format') }">
            {{placeholder.format ? placeholder.format: type === FILE.IMAGE.NAME ? 'Format： jpg. png.' : 'Format： .mp4'}}
          </span>
        </div>

        <div class="upload-component-notes mb-3">
          <span :class="{ 'text-danger': localErrors.find(i => i === 'size') }">
            {{placeholder.size? placeholder.size: type === FILE.IMAGE.NAME ? 'Size： 300 KB maximum' : `Size： ${ad_video_max_file_size} MB maximum`}}</span>
        </div>

        <div class="upload-component-notes" v-if="type === FILE.IMAGE.NAME">
          <span :class="{ 'text-danger': localErrors.find(i => i === 'dimensions') }">{{placeholder.dimensions? placeholder.dimensions: 'Dimensions：640*360 px'}}</span>
        </div>

        <div class="upload-component-notes" v-if="type === 'video'">
          <span :class="{ 'text-danger': localErrors.find(i => i === 'duration') }">
            {{placeholder.duration? placeholder.duration: `Duration：Max ${ad_video_max_file_size} Seconds`}}
          </span>
        </div>

        <div class="icon-container absolute w-full bottom-0 left-0 p-2">
          <img class="upload-icon" :src="require('@/assets/images/campaigns/upload-icon.svg')" alt="">
          <img class="upload-icon-hover" :src="require('@/assets/images/campaigns/upload-icon-hover.svg')" alt="">
        </div>
      </div>

      <div v-else class=" relative" :class="[{'video-container': type == 'video' ,'image-container':type != 'video'}]">
        <span v-if="!disabled" class="remove-icon absolute cursor-pointer" @click="handleDelete">
          <img src="https://img.icons8.com/material-outlined/24/000000/delete-sign.png"/>
        </span>

        <div v-if="type === FILE.IMAGE.NAME" :class="{container: showOverlayOverImg}">
          <img :src="file_as_data_url" class="review-img" alt="ad image">
          <a
            class="overlay flex items-center space-x-3 space-y-1 text-white"
            :class="{hidden: !showOverlayOverImg}"
            @click="cropper.show = true">

            <vs-icon icon-pack="speakol" icon="speakol-edit" class="text-white text-base font-medium"/>
            <span >{{ $t("Edit Photo") }}</span>
          </a>
        </div>

        <video-player
          v-else-if="type == 'video' && $can('speakol_dashboard') && !videoUploaded"
          class="vjs-custom-skin"
          :options="playerOptions">
        </video-player>

        <video v-else :src="file_as_data_url" class="video-thumbnail"></video>
      </div>

      <span class="mt-4 text-danger"
        v-for="(e, i) in localErrors.filter(i => !['format', 'size', 'dimensions'].includes(i))"
        :key="'upload-errors-' + i">
        {{ e }}
      </span>
    </div>

    <Cropper
      v-show="cropper.show"
      :cropper="cropper"
      @cancel-cropper="cancelCropper"
      @apply-cropper="applyCropper"
      >
    </Cropper>
  </div>
</template>

<script>
import 'video.js/dist/video-js.css'

import {videoPlayer} from 'vue-video-player'
import Cropper from '@/views/campaigns/components/steps/components/AdsetAds/components/Cropper.vue'
import {cloneDeep} from 'lodash'

import fileUtils from '@/utils/File.js'

export default {
  components: {
    videoPlayer,
    Cropper
  },
  props: {
    value: {
      required: true
    },
    accept: {
      type: String,
      default: 'image/x-png,image/jpeg'
    },
    type: {
      type: String,
      default: 'image',
      validator: val => ['image', 'video'].includes(val)
    },
    // width, height are the dimensions validation in case ths type prop is image
    width: {
      type: Number,
      default: 640
    },
    height: {
      type: Number,
      default: 360
    },
    maxSize: { // in MB
      type: Number,
      default: process.env.VUE_APP_AD_VIDEO_MAX_FILE_SIZE
    },
    showTitle: {
      type: Boolean,
      default: true
    },
    placeholder: {
      type: Object,
      default () {
        return new Object
      }
    },
    serverErrors: {
      type: Array,
      required: false
    },
    validateOnDrop: {
      type: Function,
      default (file) {
        return /\.(jpeg|png)$/.test(file.name)
      }
    },
    disabled: {
      type: Boolean,
      default: false
    },
    errorMessage: {
      type: String,
      default: 'This file type is not valid'
    }
  },
  watch: {
    value () {
      this.$emit('input', this.value)
    }
  },
  data () {
    return {
      FILE: {
        IMAGE: {
          NAME: 'image',
          FORMATS: ['image/png', 'image/jpeg']
        }
      },
      ad_video_max_file_size: process.env.VUE_APP_AD_VIDEO_MAX_FILE_SIZE,
      file_as_data_url: '',
      uploadErrors: [],
      ratio: '',
      videoUploaded: false,
      aspectRatio: 0,
      playerOptions: {
        width: 740,
        autoplay: false,
        controls: true,
        language: 'en',
        playbackRates: [0.7, 1.0, 1.5, 2.0],
        sources:
          [
            {
              src: '',
              type: 'video/mp4'
            }
          ]
      },

      showEditOnImage: false,

      cropper: {
        show: false,
        initialImg: this.value,
        resultBase64: null,
        coordinates: {},
        defaultSize: {
          width: 640,
          height: 360
        },
        backupFileURL: cloneDeep(this.value)
      }
    }
  },
  computed: {
    localErrors () {
      let errors = []
      if (this.serverErrors) {
        errors = this.serverErrors.filter(i => i !== '')
      }
      return this.uploadErrors.concat(errors)
    },

    showFileInput () {
      return (this.value === null && this.file_as_data_url !== null)
    },

    showOverlayOverImg () {
      return this.showEditOnImage && this.$hasFeature('ad-image-cropper')
    }
  },

  async mounted () {
    if (window.innerWidth < 1800) {
      this.playerOptions.width = 540
    }
    this.playerOptions.sources[0].src = this.value
    // Render the file if added from outside
    if (this.value !== null) {
      this.preview(this.value)
    }
  },
  methods: {
    async applyCropper (cropper) {
      this.file_as_data_url = cropper.resultBase64
      this.showEditOnImage = true

      await this.uploadAndPreview(
        await fileUtils.dataURLtoFile(cropper.resultBase64)
      )

      cropper.show = false
      this.cropper = cropper
    },

    async cancelCropper (cropper) {
      this.$refs['fileInput'].value = null

      const isFirstUpdate = this.file_as_data_url.startsWith('http')

      cropper.show = false
      this.cropper = cropper

      const file = isFirstUpdate ? cropper.backupFileURL : cropper.resultBase64

      this.preview(file)
    },

    handleDragover (e) {
      e.stopPropagation()
      e.preventDefault()
      e.dataTransfer.dropEffect = 'copy'
    },

    async handleDrop (e) {
      e.stopPropagation()
      e.preventDefault()

      const files = e.dataTransfer.files

      if (files.length !== 1) {
        if (!this.uploadErrors.find(i => i === 'Only support uploading one file!')) {
          this.uploadErrors.push('Only support uploading one file!')
        }

        this.$emit('input', null)

        return
      }

      const rawFile = files[0] // only use files[0]

      if (rawFile.type.includes('video')) {
        const isValidVideo = await fileUtils.checkVideoAspectRatio(rawFile)
        if (isValidVideo === false) {
          return
        }
      }

      if (typeof this.validateOnDrop === 'function' && !this.validateOnDrop(rawFile)) {
        if (!this.uploadErrors.find(i => i === 'Error uploading file')) {
          this.uploadErrors.push('Error uploading file')
        }

        this.$emit('input', null)

        return false
      }

      await this.uploadAndPreview(rawFile)
    },

    async handleClick (e) {
      this.uploadErrors = []

      const files = e.target.files

      const rawFile = files[0]

      if (!rawFile) return

      if (rawFile.type.includes('video')) {
        try {
          await fileUtils.checkVideoAspectRatio(rawFile)
        } catch (err) {
          this.uploadErrors.push('Please provide video with aspect ratio 16:9 or 4:3')

          return
        }
      }

      try {
        await fileUtils.validateSize(rawFile, this.maxSize)
      } catch (error) {
        this.uploadErrors.push('size')

        return
      }

      if (this.type === this.FILE.IMAGE.NAME) {
        if (!fileUtils.isAllowedImgFormat(rawFile, this.FILE.IMAGE.FORMATS)) {
          this.uploadErrors.push('format')
        } else {
          try {
            const valid = await fileUtils.validateDimensions(rawFile, this.width, this.height, this.$hasFeature('ad-image-cropper'))

            if (valid === 'equal') {
              await this.uploadAndPreview(rawFile)
              this.showEditOnImage = false

            } else {
              // Open the cropper popup in case if image dimensions are greater than allowed
              this.setCropper(
                await fileUtils.getBase64OfFile(rawFile)
              )
            }
          } catch (error) {
            this.uploadErrors.push('dimensions')
          }
        }
      } else if (rawFile.type === 'video/mp4') {
        await this.uploadAndPreview(rawFile)
      } else {
        this.uploadErrors.push('You can only upload video/mp4 files')
      }
    },

    async uploadAndPreview (file) {
      await this.uploadFile(file)

      this.preview(file)
    },

    async uploadFile (blob) {
      this.$refs['fileInput'].value = null // fix can't select the same excel

      this.$emit(
        'input',
        fileUtils.createFileFromBlob(blob)
      )

      this.uploadErrors = []
    },

    preview (file) {
      if (typeof file === 'string' && file.startsWith('http')) {
        this.file_as_data_url = file
        return
      }

      if (this.type !== this.FILE.IMAGE.NAME) {
        this.videoUploaded = true
      }

      const reader = new FileReader()

      reader.addEventListener('load', () => {
        this.file_as_data_url = reader.result
      })

      if (file instanceof File || file instanceof Blob) {
        reader.readAsDataURL(file)
      }
    },

    handleDelete () {
      this.$emit('input', null)
      this.$emit('delete', {type: 'remove', value: null})
    },

    setCropper (base64, show = true) {
      this.cropper.initialImg = base64
      this.cropper.show = show
    }
  }
}

</script>

<style lang="scss" scoped>

.container {
  position: relative;
  background: #323232;
  border-radius: 10px;

  .overlay {
    transition: 0.5s ease;
    opacity: 0%;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    -ms-transform: translate(-50%, -50%);
    text-align: center;
    cursor: pointer;
    width: fit-content;
  }

  &:hover .overlay {
    opacity: 1;
  }

  &:hover .review-img:not(.old-upload) {
    opacity: 0.3;
  }
}

.upload-component::v-deep {
  .video-thumbnail {
    width: 100%;
    height: 100%;
  }

  .vjs-big-play-button {
    margin: 0 !important;
    position: absolute !important;
    top: 50% !important;
    left: 50% !important;
    transform: translate(-50%, -50%) !important;
  }

  .upload-component {
    background-color: rgba(0, 0, 0, 0.02);
    border: 1px dashed rgba(0, 0, 0, 0.15);
    border-radius: 4px;

    .upload-component-notes span {
      color: #c2c2c2;
    }

    .icon-container {
      background-color: rgba(0, 0, 0, 0.02);

      .upload-icon-hover {
        display: none;
      }
    }
  }

  .upload-component:hover {
    border: 1px dashed #0092d7;
    transition: 0.2s all ease-in-out;

    .icon-container {
      background-color: #0092d7;

      .upload-icon {
        display: none;
      }

      .upload-icon-hover {
        display: inline-block;
      }
    }
  }

  .error,
  .error:hover {
    border: 1px dashed rgba(224, 32, 32, 0.5);

    .icon-container {
      background-color: rgba(224, 32, 32, 0.5);

      .upload-icon {
        display: none;
      }

      .upload-icon-hover {
        display: inline-block;
      }
    }
  }

  .image-container {
    width: 100%;
    height: auto;
    max-width: 640px;
    max-height: 640px;
    overflow: hidden;
    border-radius: 10px;

    .remove-icon {
      color: #999;
      background: rgba(255, 255, 255, 0.810588);
      border-radius: 4px;
      right: 25px;
      top: 20px;
      z-index: 49;

      img {
        padding: 4px 4px 0 4px;
        opacity: 0.5;
        width: 24px !important;
        height: 19px !important;
      }
    }

    .download-icon {
      margin-right: 20px;
      color: gray;
      background: rgba(255, 255, 255, 0.810588);
      border-radius: 4px;
      right: 55px;
      top: 20px;
      z-index: 49;

      .download-icon-img {
        padding: 7px 6px 5px 6px;
      }
    }

    img {
      border-radius: 10px;
      width: 100%;
      height: 100%;
    }
  }

  .video-container {
    max-width: 750px;
    max-height: 500px;
    overflow: hidden;
    float: left;

    .remove-icon {
      color: #999;
      background: rgba(255, 255, 255, 0.810588);
      border-radius: 4px;
      right: 25px;
      top: 20px;
      z-index: 49;

      img {
        padding: 4px 4px 0 4px;
        opacity: 0.5;
      }
    }

    .download-icon {
      margin-right: 20px;
      color: gray;
      background: rgba(255, 255, 255, 0.810588);
      border-radius: 4px;
      right: 55px;
      top: 20px;
      z-index: 49;

      .download-icon-img {
        padding: 7px 6px 5px 6px;
      }
    }

    img {
      border-radius: 10px;
    }
  }

  @media (max-width: 576px) {
    .image-container,
    .video-container {
      width: 100%;
      height: 100%;

      img {
        width: 100%;
        height: 100%;
      }
    }

    .video-js {
      width: 100% !important;
    }

    .download-icon {
      right: 17% !important;
    }
  }

  .vjs-controls-disabled {
    width: 100% !important;
  }
}
</style>
